<template>
  <div class="page-container" :class="theme === 'dark' ? 'dark' : ''">
    <div class="page-header">
      <div>
        <h2 class="page-title">字数数据看板</h2>
        <p class="page-subtitle">趋势 + 排行，一屏掌控</p>
      </div>
      <div class="header-actions">
        <el-segmented v-model="theme" :options="themeOptions" @change="onThemeChange" />
      </div>
    </div>

    <el-card class="filter-card" shadow="never">
      <div class="filter-row">
        <el-date-picker v-model="dateRange" type="daterange" range-separator="至" start-placeholder="开始日期"
          end-placeholder="结束日期" value-format="YYYY-MM-DD" />
        <el-input v-model="filters.novel_id" placeholder="小说ID（可选）" style="width:160px" clearable />
        <!-- <el-input v-model="filters.category_id" placeholder="分类ID（可选）" style="width:160px" clearable /> -->
        <el-button type="primary" @click="refreshAll">查询</el-button>
        <el-button @click="resetDate">重置</el-button>
      </div>
    </el-card>

    <div class="grid-2">
      <!-- 趋势折线图 -->
      <el-card shadow="never">
        <div class="card-title">每日字数趋势（消费 / 充值 / 余额）</div>
        <div ref="trendRef" class="chart"></div>
      </el-card>

      <!-- 小说TOP榜 -->
      <el-card shadow="never">
        <div class="card-title">
          小说消费排行榜（Top {{ novelTopLimit }}）
          <div class="right actions">
            <el-input-number v-model="novelTopLimit" :min="5" :max="50" @change="fetchNovelTop" size="small" />
            <el-button size="small" @click="exportTop('novel')">导出CSV</el-button>
          </div>
        </div>
        <div ref="novelTopRef" class="chart"></div>
      </el-card>
    </div>

    <el-card class="list-card" shadow="never">
      <el-tabs v-model="activeTab" @tab-change="fetchLeaderboard">
        <el-tab-pane label="小说排行榜（明细）" name="novel">
          <div class="toolbar">
            <div></div>
            <div>
              <el-button @click="exportTop('novel')">导出CSV</el-button>
            </div>
          </div>
          <el-table :data="novelList" border stripe v-loading="loadingNovel">
            <el-table-column prop="novel_id" label="小说ID" width="100" />
            <el-table-column prop="novel_title" label="小说标题" min-width="220" show-overflow-tooltip />
            <!-- <el-table-column prop="category_id" label="分类ID" width="100" /> -->
            <el-table-column prop="chapter_count" label="章节数" width="100" />
            <el-table-column prop="total_words" label="消费字数" width="140" />
            <el-table-column label="操作" fixed="right" width="110">
              <template #default="{ row }">
                <el-button size="small" type="primary" @click="openEditor(row.novel_id)">查看详情</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination v-model:current-page="novelPager.page" v-model:page-size="novelPager.limit"
            :total="Number(novelPager.total)" :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper" @size-change="fetchNovelList"
            @current-change="fetchNovelList" style="margin-top: 12px; justify-content: flex-end;" />
        </el-tab-pane>

        <el-tab-pane label="章节排行榜（明细）" name="chapter">
          <div class="toolbar">
            <div></div>
            <div>
              <el-button @click="exportTop('chapter')">导出CSV</el-button>
            </div>
          </div>
          <el-table :data="chapterList" border stripe v-loading="loadingChapter">
            <el-table-column prop="novel_id" label="小说ID" width="100" />
            <el-table-column prop="novel_title" label="小说标题" min-width="220" show-overflow-tooltip />
            <!-- <el-table-column prop="category_id" label="分类ID" width="100" /> -->
            <el-table-column prop="chapter_id" label="章节ID" width="100" />
            <el-table-column prop="chapter_title" label="章节标题" min-width="220" show-overflow-tooltip />
            <el-table-column prop="total_words" label="消费字数" width="140" />
            <el-table-column label="操作" fixed="right" width="110">
              <template #default="{ row }">
                <el-button size="small" type="primary"
                  @click="openEditor(row.novel_id, row.chapter_id)">查看详情</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination v-model:current-page="chapterPager.page" v-model:page-size="chapterPager.limit"
            :total="Number(chapterPager.total)" :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper" @size-change="fetchChapterList"
            @current-change="fetchChapterList" style="margin-top: 12px; justify-content: flex-end;" />
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, watch } from 'vue'
import * as echarts from 'echarts'
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'

const router = useRouter()
const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id

// ============ 主题切换（ECharts） ============
const THEME_KEY = 'chart_theme'
const themeOptions = [
  { label: '明亮', value: 'light' },
  { label: '暗黑', value: 'dark' }
]
const theme = ref(localStorage.getItem(THEME_KEY) || 'light')
function onThemeChange() {
  localStorage.setItem(THEME_KEY, theme.value)
  reinitCharts()
}

// ============ 筛选 ============
const dateRange = ref([])
const filters = reactive({ novel_id: '', category_id: '' })

function dateParams() {
  const p = { store_id: storeId }
  if (dateRange.value && dateRange.value.length === 2) {
    p.start_date = dateRange.value[0]
    p.end_date = dateRange.value[1]
  }
  if (filters.novel_id) p.novel_id = filters.novel_id
  if (filters.category_id) p.category_id = filters.category_id
  return p
}

// ============ ECharts refs & instances ============
const trendRef = ref(null)
let trendChart = null
const novelTopRef = ref(null)
let novelTopChart = null

// 折线数据
const trendData = reactive({
  dates: [],
  consume: [],
  recharge: [],
  net: [],
  balance_start: 0,
  balance_now: 0,
  is_unlimited: false,
  balance_curve: []
})

// 小说 TopN 条形图数据
const novelTopLimit = ref(10)
const novelTop = ref([])

// 明细表 & 分页
const activeTab = ref('novel')
const loadingNovel = ref(false)
const novelList = ref([])
const novelPager = reactive({ page: 1, limit: 10, total: 0 })

const loadingChapter = ref(false)
const chapterList = ref([])
const chapterPager = reactive({ page: 1, limit: 10, total: 0 })

// -------- API ----------
async function fetchTrend() {
  try {
    const params = { action: 'trend', ...dateParams() }
    const { data } = await axios.get(`${config.BASE_URL}/shop/api/word_analytics_api.php`, { params })
    if (data.code === 200) {
      const d = data.data || {}
      // 强制转 number，避免字符串导致坐标轴异常
      const toNumArr = (arr) => (Array.isArray(arr) ? arr.map(v => Number(v) || 0) : [])
      trendData.dates = Array.isArray(d.dates) ? d.dates : []
      trendData.consume = toNumArr(d.consume)
      trendData.recharge = toNumArr(d.recharge)
      trendData.net = toNumArr(d.net)
      trendData.balance_start = Number(d.balance_start) || 0
      trendData.balance_now = Number(d.balance_now) || 0
      trendData.is_unlimited = Boolean(d.is_unlimited)

      // 前端计算余额曲线
      let acc = trendData.balance_start
      const curve = []
      for (let i = 0; i < trendData.net.length; i++) {
        acc += Number(trendData.net[i]) || 0
        // 如果是无限余额，所有点都设为-1
        curve.push(trendData.is_unlimited ? -1 : Math.max(0, acc))
      }
      trendData.balance_curve = curve
      renderTrend()
    }
  } catch (e) {
    ElMessage.error('趋势数据加载失败')
  }
}

async function fetchNovelTop() {
  try {
    const params = { action: 'leaderboard_novel', ...dateParams(), page: 1, limit: novelTopLimit.value }
    const { data } = await axios.get(`${config.BASE_URL}/shop/api/word_analytics_api.php`, { params })
    if (data.code === 200) {
      novelTop.value = (data.data.list || [])
      renderNovelTop()
    }
  } catch (e) {
    ElMessage.error('小说Top加载失败')
  }
}

async function fetchNovelList() {
  try {
    loadingNovel.value = true
    const params = { action: 'leaderboard_novel', ...dateParams(), page: novelPager.page, limit: novelPager.limit }
    const { data } = await axios.get(`${config.BASE_URL}/shop/api/word_analytics_api.php`, { params })
    if (data.code === 200) {
      novelList.value = data.data.list || []
      novelPager.total = Number(data.data.pagination.total || 0)
    }
  } catch (e) {
    ElMessage.error('小说排行榜加载失败')
  } finally {
    loadingNovel.value = false
  }
}

async function fetchChapterList() {
  try {
    loadingChapter.value = true
    const params = { action: 'leaderboard_chapter', ...dateParams(), page: chapterPager.page, limit: chapterPager.limit }
    const { data } = await axios.get(`${config.BASE_URL}/shop/api/word_analytics_api.php`, { params })
    if (data.code === 200) {
      chapterList.value = data.data.list || []
      chapterPager.total = Number(data.data.pagination.total || 0)
    }
  } catch (e) {
    ElMessage.error('章节排行榜加载失败')
  } finally {
    loadingChapter.value = false
  }
}

function fetchLeaderboard() {
  if (activeTab.value === 'novel') fetchNovelList()
  else fetchChapterList()
}

function refreshAll() {
  fetchTrend()
  fetchNovelTop()
  fetchLeaderboard()
}

function resetDate() {
  dateRange.value = []
  filters.novel_id = ''
  filters.category_id = ''
  refreshAll()
}

function exportTop(type) {
  const base = dateParams()
  const q = new URLSearchParams({ ...base, action: 'export_top', type })
  const url = `${config.BASE_URL}/shop/api/word_analytics_api.php?${q.toString()}`
  window.open(url, '_blank')
}

// -------- 跳转创作页 ----------
function openEditor(novel_id, chapter_id) {
  if (!novel_id) return
  const query = { novel_id: String(novel_id) }
  if (chapter_id) query.chapter_id = String(chapter_id)
  router.push({ path: '/novel/novellist', query })
}

// -------- Charts ----------
function initTrend() {
  if (trendChart) trendChart.dispose()
  trendChart = echarts.init(trendRef.value, theme.value)
  window.addEventListener('resize', resize)
  trendChart.on('click', (params) => {
    const date = params.name
    router.push({
      path: '/novel/report',
      query: {
        start_date: date,
        end_date: date,
        tab: 'consume'
      }
    })
  })
}


function renderTrend() {
  if (!trendChart) initTrend()

  const fmt = (v) => {
    if (v === -1) return '无限'
    const n = Number(v) || 0
    if (n >= 100000000) return (n / 100000000).toFixed(1) + '亿'
    if (n >= 10000) return (n / 10000).toFixed(1) + '万'
    return n.toLocaleString()
  }

  const option = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (val) => fmt(val)
    },
    legend: {
      data: ['消费字数', '充值字数', '余额'],
      top: 10
    },
    grid: { left: 56, right: 20, top: 40, bottom: 30 },
    xAxis: {
      type: 'category',
      data: trendData.dates,
      axisLabel: {
        rotate: 45
      }
    },
    yAxis: {
      type: 'value',
      name: '字数',
      axisLabel: {
        formatter: (val) => fmt(val)
      },
      min: (range) => range.min === -1 ? -1 : Math.max(0, Math.floor(range.min)),
      max: (range) => range.max === -1 ? 0 : Math.ceil(range.max * 1.1)
    },
    series: [
      {
        name: '消费字数',
        type: 'line',
        smooth: true,
        data: trendData.consume,
        itemStyle: { color: '#f56c6c' },
        lineStyle: { color: '#f56c6c' }
      },
      {
        name: '充值字数',
        type: 'line',
        smooth: true,
        data: trendData.recharge, // 直接使用原始数据，不映射为null
        itemStyle: {
          color: trendData.recharge.some(v => v === -1) ? '#E6A23C' : '#67c23a'
        },
        lineStyle: {
          type: trendData.recharge.some(v => v === -1) ? 'dashed' : 'solid',
          color: trendData.recharge.some(v => v === -1) ? '#E6A23C' : '#67c23a'
        },
        symbol: trendData.recharge.some(v => v === -1) ? 'none' : 'circle',
        symbolSize: 6
      },
      {
        name: '余额',
        type: 'line',
        smooth: true,
        data: trendData.balance_curve,
        itemStyle: {
          color: trendData.is_unlimited ? '#E6A23C' : '#409EFF'
        },
        lineStyle: {
          type: trendData.is_unlimited ? 'dashed' : 'solid',
          color: trendData.is_unlimited ? '#E6A23C' : '#409EFF'
        },
        symbol: trendData.is_unlimited ? 'none' : 'circle',
        symbolSize: 6
      }
    ]
  }
  trendChart.setOption(option, true)
}

function initNovelTop() {
  if (novelTopChart) novelTopChart.dispose()
  novelTopChart = echarts.init(novelTopRef.value, theme.value)
  novelTopChart.on('click', (p) => {
    const item = novelTop.value[p.dataIndex]
    if (item?.novel_id) openEditor(item.novel_id)
  })
}

function renderNovelTop() {
  if (!novelTopChart) initNovelTop()
  const names = novelTop.value.map(i => i.novel_title || `小说#${i.novel_id}`)
  const vals = novelTop.value.map(i => i.total_words || 0)

  const option = {
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let result = params[0].name + '<br/>'
        params.forEach(param => {
          let value = param.value
          if (value === -1) {
            value = '无限'
          } else if (value >= 100000000) {
            value = (value / 100000000).toFixed(1) + '亿'
          } else if (value >= 10000) {
            value = (value / 10000).toFixed(1) + '万'
          } else {
            value = value.toLocaleString()
          }
          result += `${param.marker}${param.seriesName}: ${value}<br/>`
        })
        return result
      }
    },
    grid: { left: 120, right: 20, top: 20, bottom: 20 },
    xAxis: {
      type: 'value',
      name: '字数',
      axisLabel: {
        formatter: (value) => {
          if (value >= 10000) return (value / 10000).toFixed(1) + '万'
          return value
        }
      }
    },
    yAxis: {
      type: 'category',
      data: names,
      inverse: true,
      axisLabel: {
        formatter: (value) => {
          if (value.length > 15) return value.substring(0, 15) + '...'
          return value
        }
      }
    },
    series: [{
      type: 'bar',
      data: vals,
      itemStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
          { offset: 0, color: '#83bff6' },
          { offset: 0.5, color: '#188df0' },
          { offset: 1, color: '#188df0' }
        ])
      }
    }]
  }
  novelTopChart.setOption(option, true)
}

function resize() {
  trendChart && trendChart.resize()
  novelTopChart && novelTopChart.resize()
}

function reinitCharts() {
  if (trendChart) { trendChart.dispose(); trendChart = null }
  if (novelTopChart) { novelTopChart.dispose(); novelTopChart = null }
  initTrend()
  renderTrend()
  initNovelTop()
  renderNovelTop()
}

onMounted(() => {
  refreshAll()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resize)
  trendChart && trendChart.dispose()
  novelTopChart && novelTopChart.dispose()
})

watch(novelTopLimit, () => fetchNovelTop())
watch(theme, () => onThemeChange())
</script>

<style scoped lang="scss">
.page-container {
  background: linear-gradient(135deg, #f5f7fa, #e4e8ed);
  min-height: 100vh;
  transition: background .2s;
}

.page-container.dark {
  background: #0f1115;
}

.page-header {
  padding: 24px 30px;
  background: #fff;
  margin-bottom: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .05);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-container.dark .page-header {
  background: #161a23;
  color: #d8d8d8;
  box-shadow: none;
  border-bottom: 1px solid #242938;
}

.page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
}

.page-subtitle {
  margin-top: 6px;
  color: #666;
}

.page-container.dark .page-subtitle {
  color: #a0a0a0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-card {
  margin-left: 15px;
  margin-right: 15px;
  margin-top: 15px;
}

.page-container.dark .filter-card :deep(.el-card) {
  background: #161a23;
  border-color: #242938;
}

.filter-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.grid-2 {
  margin-left: 15px;
  margin-right: 15px;
  margin-top: 15px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart {
  width: 100%;
  height: 360px;
}

.card-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 600;
}

.card-title .actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.list-card {
  margin-left: 15px;
  margin-right: 15px;
  margin-top: 15px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

:deep(.el-card) {
  background: #fff;
}

.page-container.dark :deep(.el-card) {
  background: #161a23;
  border: 1px solid #242938;
  color: #d8d8d8;
}

@media (max-width: 992px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-row .el-date-picker,
  .filter-row .el-input {
    width: 100% !important;
  }
}
</style>